import MoreIcon from "@mui/icons-material/More";
import {
  getLastMonths,
  monthFormater as monthFormatter,
} from "./calendar-util";
import { getColorByPropertyValue } from "./theme.ts";
import {
  SvgBook,
  SvgDirectionCar,
  SvgHealthcare,
  SvgInsuranceCover,
  SvgMobile,
  SvgShoppingBag,
  SvgDependants,
  SvgHouse,
  SvgHome,
  SvgBed,
  SvgTv,
  SvgHandPackage,
  SvgHandyman,
  SvgClothing,
  SvgUmbrella,
  SvgSurfing,
  SvgEntertainment,
  SvgMiscellaneousExpense,
  SvgLoanRepayment,
} from "../images/expenses";

export const essentialExpenseTypes = [
  {
    key: "clothing-personal-care",
    label: "Clothing & Personal Care",
    description: "Clothing, footwear, cosmetics and personal care.",
    icon: <SvgClothing />,
  },
  {
    key: "higher-education",
    label: "Higher Education",
    description:
      "Primary, High School (Public and Private), and Higher Education (University and TAFE)",
    icon: <SvgBook />,
  },
  {
    key: "groceries",
    label: "Groceries",
    description:
      "Typical supermarket shop for groceries including food and toiletries.",
    icon: <SvgShoppingBag />,
  },
  {
    key: "medical-health-care",
    label: "Medical & Health Care",
    description:
      "Medical and health costs including doctor, dental, optical, medical equipment and pharmaceutical etc.",
    icon: <SvgHealthcare />,
  },
  {
    key: "recreation-entertainment-expenses",
    label: "Recreation & Entertainment Expenses",
    description:
      "Recreation and entertainment costs including alcohol, tobaccos, gambling, restaurants, membership fees, pet care and general entertainment.",
    icon: <SvgEntertainment />,
  },
  {
    key: "phone-internet-pay-tv-media-streaming",
    label: "Phone, Internet, Pay TV & Media Streaming",
    description:
      "Telephone accounts (home and mobile), internet, pay TV and media streaming subscriptions (such as Netflix and Spotify).",
    icon: <SvgMobile />,
  },
  {
    key: "transport",
    label: "Transport",
    description:
      "Public transport, motor vehicle running costs including fuel, servicing, registration, parking and tolls (excluding motor vehicle insurance which is categorised under insurance).",
    icon: <SvgDirectionCar />,
  },
  {
    key: "dependants-living-expenses",
    label: "Dependants' Living Expenses",
    description:
      "Costs associated with people who depend on you, including child care, school fees, and aged care.",
    icon: <SvgDependants />,
  },
  {
    key: "insurance-cost",
    label: "Insurance Cost",
    description:
      "All insurances including health, life & income, home and contents, building, motor vehicle, or other assets or general insurance you may have covered.",
    icon: <SvgInsuranceCover />,
  },
  {
    key: "property-expenses",
    label: "Property Expenses",
    description:
      "All property expenses you pay for both your primary residence and investment/holiday homes. This includes local government charges, real estate fees, water, and electricity.",
    icon: <SvgHome />,
  },
  {
    key: "rental-expenses",
    label: "Rental Expenses",
    description:
      "Rental amounts currently paid (this will be subtracted if buying a home to move into).",
    icon: <SvgHouse />,
  },
];

export const discretionaryExpenseTypes = [
  {
    key: "home-furnishings-decor",
    label: "Home Furnishings & Décor",
    description: "Home styling, decorating, and furniture",
    icon: <SvgBed />,
  },
  {
    key: "appliances-electronics",
    label: "Appliances & Electronics",
    description: "Electronic appliances for home, personal, or work.",
    icon: <SvgTv />,
  },
  {
    key: "holidays",
    label: "Holidays",
    description: "Flights and accommodation associated with travel.",
    icon: <SvgUmbrella />,
  },
  {
    key: "gifts-special-occasions",
    label: "Gifts & Special Occasions",
    description:
      "Spending on gifts, celebrations, events, and charitable donations.",
    icon: <SvgHandPackage />,
  },
  {
    key: "home-improvements-renovations",
    label: "Home Improvements & Renovations",
    description:
      "All costs related to building, maintenance, and upkeep of your property.",
    icon: <SvgHandyman />,
  },
  {
    key: "hobbies",
    label: "Hobbies",
    description:
      "Activities outside your usual routine that you do to pass the time.",
    icon: <SvgSurfing />,
  },
  {
    key: "miscellaneous-expenses",
    label: "Miscellaneous Expenses",
    description:
      "General costs that don't fit into other categories, often irregular or one-off items.",
    icon: <SvgMiscellaneousExpense />,
  },
  {
    key: "automotive-machinery",
    label: "Automotive & Machinery",
    description:
      "Covers costs related to purchasing new or used motor vehicles as well as heavy farming equipment.",
    icon: <SvgDirectionCar />,
  },
  {
    key: "loan-repayments",
    label: "Loan Repayments",
    description: "Ongoing repayments made to lenders or other debt providers.",
    icon: <SvgLoanRepayment />,
  },
];

// Merge both expense types into a single array
export const mergedExpenseTypes = [
  ...essentialExpenseTypes,
  ...discretionaryExpenseTypes,
].sort((a, b) =>
  String(a.label).localeCompare(String(b.label), undefined, {
    sensitivity: "base",
  })
);

export const getByPathOrDefault = (obj = {}, keyPath = [], defaultVal = "") => {
  let result = obj;

  for (let i = 0; i < keyPath.length; i += 1) {
    result = result[keyPath[i]];
    if (!result) {
      return defaultVal;
    }
  }

  return result || defaultVal;
};

export const sumArray = (arr) => arr.reduce((x, y) => x + y, 0);
export const sumArrays = (...arrays) => {
  const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
  const result = Array.from({ length: n });
  return result.map((_, i) =>
    arrays.map((xs) => xs[i] || 0).reduce((sum, x) => sum + x, 0)
  );
};

export const getDataLast3Months = (data) => {
  if (data) {
    const { changeHistory = [] } = data;
    return changeHistory.reduce((result, item) => {
      if (
        getLastMonths(new Date(), 3).includes(
          monthFormatter(new Date(item.date))
        )
      ) {
        return result + parseInt(item.amount, 10);
      }
      return result;
    }, 0);
  }
  return 0;
};

export const getExpensesCategoryColor = () => {
  return getColorByPropertyValue("--secondaryColor");
};

export const getExpensesCategoryIcon = (key) => {
  return (
    mergedExpenseTypes.find((type) => type.key === key)?.icon || <MoreIcon />
  );
};

export const expensesCategoryChartColor = (chartData = []) => {
  return chartData.map(() => getExpensesCategoryColor());
};
export const expensesCategoryChartIcon = (chartData = []) => {
  return chartData.map((chartItem) => getExpensesCategoryIcon(chartItem.key));
};

export const toTop5ExpenseChartDataAndOther = (chart = []) => {
  const sortedData = chart.sort(
    ({ expenseAmount: amount1 }, { expenseAmount: amount2 }) =>
      amount1 - amount2
  );
  const top5ChartData = sortedData.slice(0, 5);
  const otherChartData = sortedData.slice(5).reduce((result, item) => {
    return result + item.expenseAmount;
  }, 0);
  if (otherChartData) {
    return [
      ...top5ChartData,
      { categoryName: "All Other", expenseAmount: otherChartData },
    ];
  }
  return top5ChartData;
};
